import React from 'react';
import * as IoC from 'typescript-ioc';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { DateTimeFormatter } from '../../../../../infrastructure/implementations/date-formatter';
import { connectZoomVM } from '../../../zoom.context';
import {
    DetailsItemText
} from '../../../../global/_details/_details-item/_details-item-text/details-item-text.presentation';
import { DetailsItemLink } from '../../../../global/_details/_details-item/_details-item-link/details-item-link';
import {
    DetailsItemDateTime
} from '../../../../global/_details/_details-item/_details-item-date-time/details-item-date-time';
import { ResourcesNavigation } from '../../../../_events/_manage-event/shared/navigation/navigation';
import { ZoomMeetingWebinarWithDateFields } from './scheduled-zoom-meeting-webinar-details.viewmodel';
import './scheduled-zoom-meeting-webinar-details.scss';
import { NOT_AVAILABLE_STRING } from '../../../../../constants/common.constants';
import { LOADING, TOPIC_TEXT } from '../../../../../constants/common-labels.constants';

interface IProps {
    webinarDetails: ZoomMeetingWebinarWithDateFields;
    loading: boolean;
    error: string | null;
    intl?: IntlShape;
}

const dateTimeFormatter = IoC.Container.get<DateTimeFormatter>(DateTimeFormatter);

const ScheduledZoomMeetingWebinarDetailsPresentation = (props: IProps) => {
    const { webinarDetails, loading } = props;

    return (
        <div>
            <section className='resources-tabs horizontal-row scheduled-meetings-list__horizontal-row'>
                <ResourcesNavigation />
            </section>
            <div className='page-container'>
                <div className='zoom-meeting-webinar-details'>
                    <h1>
                        <FormattedMessage
                            id='oe.zoom.webinarDetails'
                            defaultMessage='Zoom Webinar Details'
                            description='Label text'
                        />
                    </h1>
                    {loading ?
                        <span>
                            <FormattedMessage
                                {...LOADING}
                                values={{
                                    suffix: '...'
                                }}
                            />
                        </span> :
                        <>
                            <>
                                <DetailsItemDateTime
                                    title={
                                        <FormattedMessage
                                            id='oe.datetime.start'
                                            defaultMessage='Start Datetime'
                                            description='Label text'
                                        />
                                    }
                                    time={dateTimeFormatter.dateToLocalString(webinarDetails.startTime)}
                                    timeInDifferentTZ={dateTimeFormatter.dateToLocalString(webinarDetails.startTime, webinarDetails.timeZone)}
                                    timeZone={webinarDetails.timeZone}
                                />
                                <DetailsItemDateTime
                                    title={
                                        <FormattedMessage
                                            id='oe.datetime.end'
                                            defaultMessage='End Datetime'
                                            description='Label text'
                                        />
                                    }
                                    time={dateTimeFormatter.dateToLocalString(webinarDetails.endTime)}
                                    timeInDifferentTZ={dateTimeFormatter.dateToLocalString(webinarDetails.endTime, webinarDetails.timeZone)}
                                    timeZone={webinarDetails.timeZone}
                                />
                            </>
                            <DetailsItemText
                                title={
                                    <FormattedMessage
                                        id='oe.host.email'
                                        defaultMessage='Host Email'
                                        description='Label text'
                                    />
                                }
                                value={webinarDetails.zoomUser?.email}
                            />
                            <DetailsItemText
                                title={
                                    <FormattedMessage
                                        id='oe.webinar.id'
                                        defaultMessage='Webinar ID'
                                        description='Label text'
                                    />
                                }
                                value={webinarDetails.zoomId}
                            />
                                <DetailsItemText
                                    title={
                                        <FormattedMessage
                                            id='oe.webinar.password'
                                            defaultMessage='Webinar Password'
                                            description='Label text'
                                        />
                                    }
                                    value={webinarDetails.password || NOT_AVAILABLE_STRING}
                                    copyButton={true}
                                />
                                <DetailsItemText
                                    title={
                                        <FormattedMessage
                                            id='oe.webinar.sip'
                                            defaultMessage='SIP'
                                            description='Label text'
                                        />
                                    }
                                    value={webinarDetails.sip || NOT_AVAILABLE_STRING}
                                    copyButton={true}
                                />
                                <DetailsItemText
                                    title={
                                        <FormattedMessage
                                            id='oe.webinar.hostKey'
                                            defaultMessage='Host Key'
                                            description='Label text'
                                        />
                                    }
                                    value={webinarDetails.zoomUser?.hostKey || NOT_AVAILABLE_STRING}
                                    copyButton={true}
                                />
                                <DetailsItemText
                                    title={<FormattedMessage {...TOPIC_TEXT} />}
                                    value={webinarDetails.topic || NOT_AVAILABLE_STRING}
                                />
                            <DetailsItemLink
                                title={
                                    <FormattedMessage
                                        id='oe.link.invite'
                                        defaultMessage='Invite Link'
                                        description='Label text'
                                    />
                                }
                                value={webinarDetails.inviteLink}
                            />
                            {webinarDetails.inviteLinkWithPassword ?
                                <DetailsItemLink
                                    title={
                                        <FormattedMessage
                                            id='oe.inviteLink.withEmbeddedPassword'
                                            defaultMessage='Invite Link with Embedded Password'
                                            description='Label text'
                                        />
                                    }
                                    value={webinarDetails.inviteLinkWithPassword}
                                /> :
                                <DetailsItemText
                                    title={
                                        <FormattedMessage
                                            id='oe.inviteLink.withEmbeddedPassword'
                                            defaultMessage='Invite Link with Embedded Password'
                                            description='Label text'
                                        />
                                    }
                                    value={'(Not available)'}
                                />}
                            <DetailsItemLink
                                title={
                                    <FormattedMessage
                                        id='oe.host.url'
                                        defaultMessage='Host URL'
                                        description='Label text'
                                    />
                                }
                                value={webinarDetails.startUrl}
                            />
                            {webinarDetails.status &&
                                <DetailsItemText
                                title={
                                    <FormattedMessage
                                        id='oe.common.status'
                                        defaultMessage='Status'
                                        description='Label text'
                                    />
                                }
                                value={webinarDetails.status}
                            />}
                            {webinarDetails.invitation &&
                                <DetailsItemText
                                    title={
                                        <FormattedMessage
                                            id='oe.webinar.invitation'
                                            defaultMessage='Webinar Invitation'
                                            description='Label text'
                                        />
                                    }
                                    value={webinarDetails.invitation}
                                    className='invitation'
                                    copyButton
                                />}
                        </>
                    }
                </div>
            </div>
        </div>
    );
};

const scheduledZoomMeetingWebinarDetailsMapperFunction = zoomVM => {
    const vm = zoomVM.getScheduledMeetingWebinarDetailsVM();
    const webinarDetails = vm.getCurrentMeetingWebinarDetails();
    const loading = vm.isLoading();
    const error = vm.getError();
    return {
        webinarDetails: webinarDetails,
        loading: loading,
        error: error
    };
};

const ScheduledZoomMeetingWebinarDetails =
    injectIntl(connectZoomVM<IProps>(ScheduledZoomMeetingWebinarDetailsPresentation, scheduledZoomMeetingWebinarDetailsMapperFunction));

export { ScheduledZoomMeetingWebinarDetails, scheduledZoomMeetingWebinarDetailsMapperFunction, ScheduledZoomMeetingWebinarDetailsPresentation };
